/**
 * The per-event reaction pair shown across every event surface (list rows,
 * cards, the detail modal, and map popups). Replaces the older trio of
 * FavoriteButton (★) + TasteButton (👍) + NotInterestedButton (👎) with a
 * simpler, clearer two-state model:
 *
 *  - 👍 **Save** — bookmarks the event (drives the "Saved / Favorites" filter)
 *    AND records a positive signal that trains recommendations.
 *  - 👎 **Not interested** — the negative recommender signal.
 *
 * The two are mutually exclusive (see `lib/favorites.tsx#setEventReaction`).
 *
 * Hidden for anonymous users (consistent with the other per-user controls).
 *
 * Accessibility:
 *  - Real <button>s with `aria-pressed` for the toggle state.
 *  - Dynamic `aria-label` always includes the visible event title.
 *  - Icons use `currentColor` so they adapt to dark mode + forced-colors.
 *  - `variant="labeled"` shows an icon + text label (the label auto-collapses
 *    to icon-only under ~576px via CSS); `variant="icon"` is always icon-only
 *    for tight surfaces such as map popups.
 */
import { useIsAuthenticated } from '@azure/msal-react';
import type { ExtractedEvent } from '../types';
import { useFavorites } from '../lib/favorites';

interface Props {
  event: Pick<ExtractedEvent, 'rowKey' | 'title'>;
  /** Presentation. Defaults to labeled (icon + text). */
  variant?: 'labeled' | 'icon';
  /** Extra classes for the wrapping group. */
  className?: string;
  /** Stop clicks from bubbling into a clickable row/card. Default true. */
  stopPropagation?: boolean;
}

export default function EventReactionButtons({
  event,
  variant = 'labeled',
  className,
  stopPropagation = true
}: Props) {
  const isAuthed = useIsAuthenticated();
  const { isSaved, isNotInterested, setEventReaction, ready } = useFavorites();
  if (!isAuthed) return null;

  const saved = isSaved(event.rowKey);
  const notInterested = isNotInterested(event.rowKey);
  const iconOnly = variant === 'icon';
  const groupClass =
    `mh-event-actions${iconOnly ? ' mh-event-actions--icon' : ''}${className ? ` ${className}` : ''}`;

  return (
    <div className={groupClass} role="group" aria-label="Your reaction to this event">
      <button
        type="button"
        className="mh-action-btn mh-action-btn--save"
        aria-pressed={saved}
        aria-label={saved ? `Remove ${event.title} from saved` : `Save ${event.title}; also improves your recommendations`}
        title={
          saved
            ? 'Saved — bookmarked, used to tune your recommendations, and we\'ll remind you the day before. Click to remove.'
            : 'Save this event — bookmark it, get a day-before reminder, and see more like it in your recommendations.'
        }
        disabled={!ready}
        onClick={(e) => {
          if (stopPropagation) {
            e.stopPropagation();
            e.preventDefault();
          }
          void setEventReaction(event.rowKey, saved ? 'none' : 'save');
        }}
      >
        <i className={`bi ${saved ? 'bi-hand-thumbs-up-fill' : 'bi-hand-thumbs-up'}`} aria-hidden="true" />
        <span className="mh-action-btn__label">{saved ? 'Saved' : 'Save'}</span>
      </button>
      <button
        type="button"
        className="mh-action-btn mh-action-btn--dislike"
        aria-pressed={notInterested}
        aria-label={
          notInterested
            ? `Remove ${event.title} from not interested`
            : `Mark ${event.title} as not interested`
        }
        title={
          notInterested
            ? '“Not interested” — we’ll show fewer events like this. Click to undo.'
            : 'Not interested — see fewer events like this in your recommendations.'
        }
        disabled={!ready}
        onClick={(e) => {
          if (stopPropagation) {
            e.stopPropagation();
            e.preventDefault();
          }
          void setEventReaction(event.rowKey, notInterested ? 'none' : 'notInterested');
        }}
      >
        <i className={`bi ${notInterested ? 'bi-hand-thumbs-down-fill' : 'bi-hand-thumbs-down'}`} aria-hidden="true" />
        <span className="mh-action-btn__label">Not interested</span>
      </button>
    </div>
  );
}
